import { Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/modules/auth/store/auth.store";
import type { ReportFilterState } from "../hooks/useReportFilters";
import type { ReportSummary } from "../types";
import { davomatFoizi, foizMatni } from "../utils/summaryDerive";

const { Title, Text } = Typography;

interface Props {
	title: string;
	filters: ReportFilterState;
	total: number;
	page: number;
	limit: number;
	shownCount: number;
	employeeLabel?: string;
	departmentLabel?: string;
	zoneLabel?: string;
	/**
	 * ⚠️ Xulosasiz hisobotda (`descriptor.hasSummary === false`) blok
	 * UMUMAN chizilmaydi — «0%» bilan EMAS.
	 */
	summary?: ReportSummary;
}

/**
 * Chop etish sarlavhasi — ekranda KO'RINMAYDI (`print.css` dagi
 * `.skud-print-only`).
 *
 * ⚠️ **Auditga chidamlilik uchun to'rt narsa yoziladi:** hisobot nomi,
 * sana oralig'i va qo'llangan filtrlar, chop etilgan vaqt, chop etgan
 * foydalanuvchi. Qog'ozdagi jadval kontekstsiz qolsa, uni keyinchalik
 * boshqa oraliqning natijasi deb o'qish mumkin bo'lardi — chop etilgan
 * hisobot esa aynan dalil sifatida saqlanadi (TT §4.16).
 *
 * ⚠️ Filtr qiymatlari UUID emas, YORLIQ bilan chiqadi: qog'ozda
 * `11111111-…` hech qanday ma'lumot bermaydi. Yorliqni chaqiruvchi
 * beradi — u tanlov ro'yxatlarini allaqachon o'qigan.
 *
 * ⚠️ **Qatorlar soni ORALIQ bilan yoziladi, yolg'iz jami bilan EMAS.**
 * Chop etishga faqat JORIY sahifa tushadi (`print.css` sahifalashni
 * yashiradi, lekin qatorlarni ko'paytirmaydi), ya'ni «Jami qator: 5000»
 * sarlavhasi ostidagi 50 qatorli qog'oz butun hisobot deb o'qilardi.
 */
export function ReportPrintView({
	title,
	filters,
	total,
	page,
	limit,
	shownCount,
	employeeLabel,
	departmentLabel,
	zoneLabel,
	summary,
}: Props) {
	const { t } = useTranslation("reports");
	const user = useAuthStore((state) => state.user);

	return (
		<div className="skud-print-only mb-4">
			<Title level={4} className="!mb-1">
				{title}
			</Title>

			<div>
				<Text>{t("print.period", { from: filters.dateFrom, to: filters.dateTo })}</Text>
			</div>

			{employeeLabel ? (
				<div>
					<Text>{t("print.employee", { name: employeeLabel })}</Text>
				</div>
			) : null}

			{departmentLabel ? (
				<div>
					<Text>{t("print.department", { name: departmentLabel })}</Text>
				</div>
			) : null}

			{zoneLabel ? (
				<div>
					<Text>{t("print.zone", { name: zoneLabel })}</Text>
				</div>
			) : null}

			<div>
				<Text>
					{shownCount > 0
						? t("print.rowsRange", {
								total,
								from: (page - 1) * limit + 1,
								to: (page - 1) * limit + shownCount,
							})
						: t("print.rowsTotal", { total })}
				</Text>
			</div>

			{/*
			 * ⚠️⚠️ **KPI SONLARI QOG'OZGA TUSHADI, DIAGRAMMALAR YO'Q.**
			 * Chop etilgan hujjat audit dalili (TT §4.16) va bu to'rt son
			 * dalilning bir qismi. Diagrammalar esa brauzer chop etishida
			 * ishonchsiz: fon ranglari `print-color-adjust` siz tushib
			 * qoladi va ustunlar bo'sh to'rtburchakka aylanadi — shuning
			 * uchun ular `ReportDetailPage` da `skud-no-print` o'ramida.
			 *
			 * ⚠️ Foiz `foizMatni()` dan — ekrandagi karta bilan AYNAN bir
			 * xil satr. Bu yerda o'z formati yozilsa, qog'ozdagi «92%»
			 * bilan ekrandagi «92,4%» ajralardi.
			 */}
			{summary ? (
				<div>
					<Text>
						{t("print.summaryLine", {
							rate: foizMatni(davomatFoizi(summary.totals.presentDays, summary.totals.countedDays)),
							lateMin: summary.totals.totalLateMin,
							absent: summary.totals.absentDays + summary.totals.excusedDays,
							excused: summary.totals.excusedDays,
						})}
					</Text>

					<div>
						{/*
						 * ⚠️⚠️ Usiz «O'rtacha davomat 92,4%» sarlavhasi ostida
						 * 50 qator turadi va o'quvchi foizni SHU 50 qatorniki
						 * deb o'qiydi — yuqoridagi «Qatorlar soni oraliq bilan
						 * yoziladi» qoidasining AYNAN takrori.
						 */}
						<Text type="secondary">{t("print.summaryNote", { total, page })}</Text>
					</div>
				</div>
			) : null}

			<div>
				<Text type="secondary">
					{t("print.printedAt", {
						date: dayjs().format("YYYY-MM-DD HH:mm"),
						// ⚠️ Spec §2.7 «ism, bo'lmasa telefon»: auth `User` da
						// (`shared/src/types/user.ts`) ism maydoni YO'Q, ya'ni
						// bugun telefon — yagona identifikator. Maydon
						// qo'shilsa shu yer va `ReportPrintView.test.tsx`
						// dagi qorovul birga yangilanadi.
						user: user?.phone ?? t("print.unknownUser"),
					})}
				</Text>
			</div>
		</div>
	);
}
